import React, { useRef, useEffect, useCallback, useState, useLayoutEffect, memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { useSelectionStore, getMagneticZoom } from '@/stores/selectionStore';
import { useNodeStore } from '@/stores/nodeStore';
import { useShallow } from 'zustand/react/shallow';

interface MagneticHandleProps {
  type: 'target' | 'source';
  position: Position;
  nodeId: string;
  cardHeight?: number;
  cardWidth?: number;
  category?: 'product' | 'competitor';
  offsetPercent?: number;
  /** 手柄旁常驻指示语句（实时由调用方计算传入）；未传时回退到类别名 */
  indicator?: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  product: '产品图',
  competitor: '竞品图',
};

const MagneticHandleInner: React.FC<MagneticHandleProps> = ({
  type,
  position,
  nodeId,
  cardHeight = 236,
  cardWidth = 420,
  category,
  offsetPercent = 50,
  indicator,
}) => {
  const handleRef = useRef<HTMLDivElement>(null);
  const originalCenterRef = useRef<{ x: number; y: number } | null>(null);
  const isSnappedRef = useRef(false);
  // 标记当前 handle 是否处于连线状态，用于 handleMouseUp 的 guard
  // 避免全局 mouseup 被其他 handle 实例错误响应
  const isConnectingRef = useRef(false);
  const [isSnapped, setIsSnapped] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [snapOffset, setSnapOffset] = useState({ x: 0, y: 0 });
  // 强制刷新计数器：updatePositionNextFrame 设置 originalCenterRef 后触发重渲染
  const [refreshTick, setRefreshTick] = useState(0);

  const handleId = category ? `${nodeId}-${type}-${position}-${category}` : `${nodeId}-${type}-${position}`;

  const {
    mouseX,
    mouseY,
    activeHandleId,
    isConnecting,
    isNodeDragging,
    registerHandle,
    unregisterHandle,
    setActiveHandle,
    setConnecting,
    initGlobalListener,
  } = useSelectionStore(
    useShallow(s => {
      if (s.activeHandleId !== handleId) {
        return {
          mouseX: 0, mouseY: 0,
          activeHandleId: s.activeHandleId,
          isConnecting: s.isConnecting,
          isNodeDragging: s.isNodeDragging,
          registerHandle: s.registerHandle,
          unregisterHandle: s.unregisterHandle,
          setActiveHandle: s.setActiveHandle,
          setConnecting: s.setConnecting,
          initGlobalListener: s.initGlobalListener,
        };
      }
      return {
        mouseX: s.mouseX, mouseY: s.mouseY,
        activeHandleId: s.activeHandleId,
        isConnecting: s.isConnecting,
        isNodeDragging: s.isNodeDragging,
        registerHandle: s.registerHandle,
        unregisterHandle: s.unregisterHandle,
        setActiveHandle: s.setActiveHandle,
        setConnecting: s.setConnecting,
        initGlobalListener: s.initGlobalListener,
      };
    })
  );

  const updatePosition = useCallback(() => {
    const handleEl = handleRef.current;
    if (!handleEl) return;
    if (isSnappedRef.current) return;

    const rect = handleEl.getBoundingClientRect();
    const currentX = rect.left + rect.width / 2;
    const currentY = rect.top + rect.height / 2;

    originalCenterRef.current = { x: currentX, y: currentY };
    registerHandle(handleId, currentX, currentY);
  }, [handleId, registerHandle]);

  // 延迟一帧再更新原点：用于 originalCenterRef 缺失时（如首次挂载或重新进入吸附）
  // 等 DOM 稳定后读取真实原点，并触发重渲染让 A 分支 effect 重新执行跟随计算
  const updatePositionNextFrame = useCallback(() => {
    requestAnimationFrame(() => {
      const handleEl = handleRef.current;
      if (!handleEl) return;
      if (isSnappedRef.current) return;
      const rect = handleEl.getBoundingClientRect();
      const currentX = rect.left + rect.width / 2;
      const currentY = rect.top + rect.height / 2;
      originalCenterRef.current = { x: currentX, y: currentY };
      registerHandle(handleId, currentX, currentY);
      // 触发重渲染让 A 分支 effect 重新执行（originalCenterRef 是 ref，变化不触发渲染）
      // 用一个专用的 state 计数器强制更新
      setRefreshTick(t => t + 1);
    });
  }, [handleId, registerHandle]);

  // 方案 3：画布 zoom/平移变化时的 handle 位置更新已移至 FlowEditor 全局订阅
  // 之前每个 MagneticHandle 都独立订阅 rfStore.transform，200 个 handle → 200 个订阅器 + 200 个 RAF
  // 现在由 FlowEditor 单一全局订阅，通过 data-handle-id 批量查询 DOM 并更新位置
  // 注意：节点拖拽的位置更新仍保留在这里（useNodeStore.subscribe），因为拖拽时只有 1 个节点在动，
  // 不需要批量更新，且需要保持实时性（无 RAF 延迟）

  // 监听节点位置变化（拖动），实时更新 handle 缓存位置
  // 否则拖动后 handlePositions 中是旧坐标，鼠标移到 handle 视觉位置不触发磁吸
  useEffect(() => {
    let prevNode = useNodeStore.getState().nodeMap.get(nodeId);
    const unsub = useNodeStore.subscribe((state) => {
      const node = state.nodeMap.get(nodeId);
      if (!node || !prevNode) { prevNode = node; return; }
      if (node.x !== prevNode.x || node.y !== prevNode.y) {
        prevNode = node;
        updatePosition();
      }
    });
    return unsub;
  }, [nodeId, updatePosition]);

  useEffect(() => {
    initGlobalListener();
  }, [initGlobalListener]);

  useLayoutEffect(() => {
    updatePosition();
  }, [cardHeight, cardWidth, updatePosition]);

  useEffect(() => {
    // 只负责卸载时清理，不注册初始位置
    // useLayoutEffect 已注册真实坐标，这里 registerHandle(0,0) 会覆盖为屏幕左上角导致磁吸失效
    return () => {
      unregisterHandle(handleId);
    };
  }, [handleId, unregisterHandle]);

  useEffect(() => {
    const isActive = activeHandleId === handleId;
    if (isActive && !isConnecting) {
      // 进入吸附前同步刷新原点：方案 3 移除了 rfStore.subscribe，
      // 画布 zoom/平移后 originalCenterRef 不再自动更新，会变得过时。
      // 此时 snapOffset 必为 0（C 分支已重置），DOM getBoundingClientRect() 是真实原点。
      // 仅在未吸附时刷新：吸附中 DOM 已被 snapOffset 偏移，读取会得到错误原点。
      if (!isSnappedRef.current) {
        const handleEl = handleRef.current;
        if (handleEl) {
          const rect = handleEl.getBoundingClientRect();
          originalCenterRef.current = {
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2,
          };
        }
      }

      const center = originalCenterRef.current;
      if (!center) {
        // 原点缺失：用 RAF 等待 DOM 中 snapOffset 复位后再读取真实原点
        // 同步读取会读到偏移位置（刚退出吸附时 snapOffset=0 还未应用到 DOM）
        updatePositionNextFrame();
        return;
      }

      // 进入吸附状态：标记 snapped，防止 updatePosition 覆盖原点基准
      if (!isSnappedRef.current) {
        isSnappedRef.current = true;
        setIsSnapped(true);
        setIsHovered(true);
      }

      // handle 中心精确吸附到鼠标位置：
      // 屏幕坐标系下 handle 中心 = center + snapOffset（snapOffset 是 translate 的屏幕像素值）
      // 要让 handle 中心 = mouse，即 snapOffset = mouse - center
      // 但 translate 的 CSS 值会再被 zoom 缩放，所以 translate 值 = snapOffset / zoom
      // 这里 snapOffset 直接存屏幕像素差值（mouse - center），应用时除以 zoom
      // 不做 clamp：吸附激活时（鼠标在 snapRadius 内）handle 十字中心必须精确等于鼠标位置
      const dx = mouseX - center.x;
      const dy = mouseY - center.y;
      setSnapOffset({ x: dx, y: dy });
    } else if (isActive) {
      // 正在连线状态：handle 回到原点，不跟随鼠标
      if (isSnappedRef.current) {
        setIsHovered(true);
        setIsSnapped(false);
        isSnappedRef.current = false;
        setSnapOffset({ x: 0, y: 0 });
        // 不更新 originalCenterRef：退出吸附后 handle 视觉回到原点，
        // 原点基准保持不变即可，避免 DOM 异步导致读到偏移位置
      }
    } else {
      // 非激活状态：仅在一次过渡时重置，避免每帧重复 setState 触发渲染
      if (isSnappedRef.current || isHovered || isSnapped) {
        setIsHovered(false);
        setIsSnapped(false);
        isSnappedRef.current = false;
        setSnapOffset({ x: 0, y: 0 });
        // 不更新 originalCenterRef：保持吸附前的原点基准
      }
    }
  }, [mouseX, mouseY, activeHandleId, isConnecting, handleId, updatePosition, updatePositionNextFrame, isHovered, isSnapped, refreshTick]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    // 设置 activeHandleId 确保进入 effect B 分支（而非 C 分支被重置）
    setActiveHandle(handleId);
    setConnecting(true);
    isConnectingRef.current = true;
    // 不设置 isSnapped：连线时 handle 回到原点，由 effect B 分支处理重置
    // 设置 isSnapped=true 会被 B 分支立即重置为 false，导致视觉闪烁
  }, [setConnecting, setActiveHandle, handleId]);

  // 参考"引用为参考"按钮：用原生 onMouseEnter/onMouseLeave 直接激活
  // 不依赖全局 mousemove + 位置缓存计算，确保鼠标进入 handle 元素时 100% 激活
  const handleMouseEnter = useCallback(() => {
    if (isConnecting) return;
    // 节点拖动中磁吸已冻结：拖动无 pointer capture，鼠标快速扫过圆点会误触发 mouseenter，
    // 此时 originalCenterRef 是拖动前旧坐标，会产生巨大 snapOffset 把圆点拉离节点
    if (isNodeDragging) return;
    if (isSnappedRef.current) return;
    // 同步刷新原点：吸附期间画布可能平移/缩放，旧 originalCenterRef 可能过时
    // 直接读取当前 DOM 位置（此时 snapOffset=0，DOM 是真实原点）
    const handleEl = handleRef.current;
    if (handleEl) {
      const rect = handleEl.getBoundingClientRect();
      const currentX = rect.left + rect.width / 2;
      const currentY = rect.top + rect.height / 2;
      originalCenterRef.current = { x: currentX, y: currentY };
      registerHandle(handleId, currentX, currentY);
    }
    setActiveHandle(handleId);
  }, [isConnecting, isNodeDragging, handleId, registerHandle]);

  const handleMouseLeave = useCallback(() => {
    if (isConnecting) return;
    // 磁吸状态下不取消激活：handle 跟随鼠标移动时，鼠标可能相对移出 handle 元素边界
    // 磁吸的释放应由 releaseRadius（240px）控制，而非 handle 元素的 mouseLeave
    if (isSnappedRef.current) return;
    setActiveHandle(null);
  }, [isConnecting, setActiveHandle]);

  const handleMouseUp = useCallback(() => {
    // 只在当前 handle 处于连线状态时重置，避免干扰其他 handle
    // 用 isConnectingRef（而非 isSnappedRef）判断：连线时 isSnappedRef 已被 B 分支重置为 false
    if (!isConnectingRef.current) return;
    isConnectingRef.current = false;
    setConnecting(false);
    setIsSnapped(false);
    isSnappedRef.current = false;
    setSnapOffset({ x: 0, y: 0 });
    setActiveHandle(null);
  }, [setConnecting, setActiveHandle]);

  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseUp]);

  const baseSize = 40;
  const hoverScale = 1.25;
  const snappedScale = 1.35;

  const currentScale = isSnapped ? snappedScale : (isHovered ? hoverScale : 1);

  const isLeft = position === Position.Left;
  const isRight = position === Position.Right;

  return (
    <div
      className={`absolute z-30 ${isLeft ? 'left-0' : isRight ? 'right-0' : ''}`}
      style={{
        top: `${offsetPercent}%`,
        transform: 'translateY(-50%)',
      }}
      onMouseDown={handleMouseDown}
    >
      {/* 常驻指示语句：位于手柄圆点左侧的画布区域（不随悬停消失），悬停时仅高亮 */}
      {category && (
        <div
          className={`pointer-events-none absolute whitespace-nowrap rounded-full border bg-[#262626]/95 px-2.5 py-1 text-[11px] leading-4 transition-colors duration-150 ${
            isHovered ? 'border-white/40 text-white/95' : 'border-white/20 text-white/70'
          }`}
          style={{ right: '44px', top: '50%', transform: 'translateY(-50%)' }}
        >
          {indicator ?? CATEGORY_LABELS[category]}
        </div>
      )}
      <div
        ref={handleRef}
        data-handle-id={handleId}
        style={{
          position: 'absolute',
          width: baseSize,
          height: baseSize,
          left: isLeft ? -baseSize : undefined,
          right: isRight ? -baseSize : undefined,
          top: '50%',
          // 吸附偏移与缩放放在同一层级，避免 transform 嵌套导致十字中心与鼠标中心不重合
          transform: `translateY(-50%) translate(${snapOffset.x / getMagneticZoom()}px, ${snapOffset.y / getMagneticZoom()}px) scale(${currentScale})`,
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Handle
          type={type}
          position={position}
          id={handleId}
          className={`!rounded-full !transition-all !duration-150 !cursor-default !p-0 ${
            isSnapped
              ? '!bg-white !border-2 !border-white !shadow-[0_0_12px_rgba(34,211,238,0.5)]'
              : isHovered
                ? '!bg-[#262626] !border !border-white/50'
                : '!bg-[#262626] !border !border-white/20'
          }`}
          style={{
            width: baseSize,
            height: baseSize,
            // 覆盖 React Flow 默认的 handle-left/handle-right 定位（left:0/right:0 + translate(±50%,-50%)）
            // 默认定位会让 Handle 中心偏移 inner div 中心 baseSize/2，导致吸附时十字中心与鼠标不对齐
            // 这里统一用 left:50% + translate(-50%,-50%) 让 Handle 中心精确对齐 inner div 中心
            left: '50%',
            right: 'auto',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <div className="w-full h-full flex items-center justify-center pointer-events-none">
            <svg
              width={baseSize * 0.4}
              height={baseSize * 0.4}
              viewBox="0 0 14 14"
              fill="none"
            >
              <path
                d="M7 3V11M3 7H11"
                stroke={isSnapped ? 'rgba(0,0,0,0.9)' : 'rgba(255,255,255,0.5)'}
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
        </Handle>
      </div>
    </div>
  );
};

export const MagneticHandle = memo(MagneticHandleInner, (prevProps, nextProps) => {
  return (
    prevProps.type === nextProps.type &&
    prevProps.position === nextProps.position &&
    prevProps.nodeId === nextProps.nodeId &&
    prevProps.cardHeight === nextProps.cardHeight &&
    prevProps.cardWidth === nextProps.cardWidth &&
    prevProps.category === nextProps.category &&
    prevProps.offsetPercent === nextProps.offsetPercent &&
    prevProps.indicator === nextProps.indicator
  );
});
